import { useId } from 'react';
import { BoltIcon, ChatBubbleLeftRightIcon, FaceSmileIcon, QueueListIcon } from '@heroicons/react/24/outline';
import type { SecretaryPersonaProfile } from '../../../shared/secretaryPreferences';
import { i18nService } from '../../services/i18n';

/** BoMate 的四组个性选项；保留图标、说明和卡片选择，使用原生单选支持键盘操作。 */
const groups = [
  { key: 'speechStyle', values: ['warm', 'concise', 'natural'], Icon: ChatBubbleLeftRightIcon },
  { key: 'responseDetail', values: ['brief', 'balanced', 'detailed'], Icon: QueueListIcon },
  { key: 'humorLevel', values: ['none', 'light', 'playful'], Icon: FaceSmileIcon },
  { key: 'initiativeLevel', values: ['responsive', 'moderate', 'proactive'], Icon: BoltIcon },
] as const;

/** 纯受控编辑器，不负责请求或保存；可在设置页与首次引导中复用。 */
export default function SecretaryPersonaEditor({ profile, onChange, disabled = false }: {
  profile: SecretaryPersonaProfile;
  /** 返回包含单项变更的完整草稿；调用方决定何时保存。 */
  onChange: (profile: SecretaryPersonaProfile) => void;
  disabled?: boolean;
}) {
  const id = useId();
  const t = (key: string) => i18nService.t(`secretaryPreference_${key}`);
  return <div className="space-y-5">{groups.map(({ key, values, Icon }) => <fieldset key={key} disabled={disabled} className="min-w-0">
    <legend className="mb-2 flex items-center gap-2 text-sm font-medium"><Icon className="h-4 w-4 text-claude-textSecondary dark:text-claude-darkTextSecondary" />{t(key)}</legend>
    <div className="grid grid-cols-1 min-[480px]:grid-cols-3 gap-2">{values.map(value => <label key={value} className="relative min-w-0 cursor-pointer">
      <input type="radio" className="peer sr-only" name={`${id}-${key}`} value={value} checked={profile[key] === value}
        aria-label={t(value)} aria-describedby={`${id}-${value}-hint`} onChange={() => onChange({ ...profile, [key]: value })} />
      <span className="flex h-full flex-col gap-1 rounded-xl border border-claude-border dark:border-claude-darkBorder px-3 py-3 transition-colors duration-200 motion-reduce:transition-none hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover peer-checked:border-claude-accent peer-checked:bg-claude-accent/10 peer-focus-visible:ring-2 peer-focus-visible:ring-claude-accent peer-disabled:cursor-wait peer-disabled:opacity-60">
        <span className="text-sm font-medium">{t(value)}</span><span id={`${id}-${value}-hint`} className="text-xs leading-5 text-claude-textSecondary dark:text-claude-darkTextSecondary">{t(`${value}Hint`)}</span>
      </span>
    </label>)}</div>
  </fieldset>)}</div>;
}
